/**
 * The one way to hear Tibbs: a button, like Single Didge's "Blair's Readout".
 * Idle → "Hear Tibbs"; while the clips load → a waiting line; while he talks →
 * Stop; when audio is locked (an online call before this device was ever
 * tapped) → Play; afterward → Replay. The caption sits under it.
 */
import type { TibbsStatus } from '../../tibbs/useTibbs';
import { Tibbs } from '../Art';
import { AmphoraIcon } from '../Icons';

export function TibbsButton({
  status,
  kind,
  onRequest,
  onStop,
  onResume,
  className = '',
}: {
  status: TibbsStatus;
  kind: 'readout' | 'ceremony';
  onRequest: () => void;
  onStop: () => void;
  onResume: () => void;
  className?: string;
}) {
  const icon = kind === 'ceremony' ? <AmphoraIcon size={20} /> : <Tibbs pose="wry" size={26} className="tibbs-btn-face" />;
  const label = kind === 'ceremony' ? 'Presentation of the Golden Amphora' : 'Hear Tibbs';
  const again = kind === 'ceremony' ? 'Replay the presentation' : 'Hear Tibbs again';
  const common = `btn tibbs-btn tb-${kind}${className ? ` ${className}` : ''}`;
  switch (status) {
    case 'loading':
      return (
        <button type="button" className={`${common} live`} disabled aria-busy="true">
          <span className="spinner small" aria-hidden="true" /> Tibbs is clearing his throat…
        </button>
      );
    case 'playing':
      return (
        <button type="button" className={`${common} live`} onClick={onStop} aria-label="Stop Tibbs">
          <span className="tibbs-stop" aria-hidden="true" /> Stop
        </button>
      );
    case 'blocked':
      return (
        <button type="button" className={`${common} live`} onClick={onResume}>
          {icon} Play
        </button>
      );
    case 'done':
      return (
        <button type="button" className={common} onClick={onRequest}>
          {icon} {again}
        </button>
      );
    default:
      return (
        <button type="button" className={common} onClick={onRequest}>
          {icon} {label}
        </button>
      );
  }
}

/** Tibbs's current line, in one italic line (screen readers get it from the table's live region). */
export function TibbsCaption({ text }: { text: string | null }) {
  return (
    <p className={`tibbs-caption${text ? ' on' : ''}`} aria-hidden="true">
      {text ? <span key={text}>{text}</span> : null}
    </p>
  );
}
